import {
  Box,
  useMediaQuery,
  useTheme,
  IconButton,
  AppBar,
  Toolbar,
  Typography,
} from "@mui/material";
import { Menu as MenuIcon } from "@mui/icons-material";
import { Outlet } from "react-router-dom";
import { DrawerDesktop } from "../navigation/DrawerDesktop";
import { DrawerMobile } from "../navigation/DrawerMobile";
import type { NavItems } from "../navigation/navItems";
import { useDrawer } from "src/utils/useDrawer";

/**
 * Main application layout with navigation drawer.
 *
 * Features:
 * - Responsive navigation (permanent drawer on desktop, temporary on mobile)
 * - Mobile: App bar with menu button
 * - Desktop: Permanent collapsible sidebar
 * - Content area adjusts based on drawer state
 *
 * `navItems` come from the shell, already filtered to what the user can open.
 */
export function AppLayout({ navItems }: { navItems: NavItems }) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { show } = useDrawer();

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      {/* Mobile App Bar */}
      {isMobile && (
        <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1 }}>
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={show}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
            <Typography variant="h6" noWrap component="div">
              Customer Portal
            </Typography>
          </Toolbar>
        </AppBar>
      )}

      {/* Navigation Drawers */}
      {isMobile ? <DrawerMobile navItems={navItems} /> : <DrawerDesktop navItems={navItems} />}

      {/* Main Content Area.
          Asymmetric inset matching twosion-ui / insights-home PageLayoutDesktop:
          16px left, ~4px right (scrollbar side), 40px top on desktop. Pages add
          their own max-width + centring on top. */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          pl: 2,
          pr: { xs: 2, md: 0.5 }, // symmetric on mobile; ~4px scrollbar gutter on desktop
          pt: { xs: 2, md: 5 },
          pb: 2,
          width: "100%",
          mt: isMobile ? "64px" : 0, // Account for mobile app bar
        }}
      >
        <Outlet />
      </Box>
    </Box>
  );
}
